import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  MapPin,
  Car,
  Smartphone,
  PhoneCall,
  Mail,
  Loader2,
  Camera,
  CheckCircle2,
  AlertCircle,
  Upload,
  User,
  ClipboardList,
  Fingerprint,
  ChevronRight,
  LogOut,
  Calendar,
  Image as ImageIcon,
  XCircle,
  Fuel,
  Receipt,
  FileText,
  UserCheck,
  FolderOpen,
} from "lucide-react";

import { toast } from "sonner";
import Swal from "sweetalert2";
import Banner from "../assets/home-image.png";
import Activity from "../components/Dashboardloader.jsx";
import TripLoader from "../components/TripLoader.jsx";
import api, { IMAGE_BASE_URL } from "../api/Api.js";
import { socket } from "../api/socket";

// --- IMAGE COMPRESSION UTILITY (Target ~100KB) ---
const compressImage = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        // Reduced max width for smaller file size
        const MAX_WIDTH = 800;
        const scaleSize = MAX_WIDTH / img.width;
        const newWidth = img.width > MAX_WIDTH ? MAX_WIDTH : img.width;
        const newHeight =
          img.width > MAX_WIDTH ? img.height * scaleSize : img.height;

        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // Quality 0.5 usually results in <100KB for 800px images
        ctx.canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Compression failed"));
              return;
            }
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          0.5,
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem("driverToken"));

  // Refs for auto-opening camera
  const odoCameraRef = useRef(null);

  // UI & Logic States
  const [gpsData, setGpsData] = useState(null);
  const [locationAddress, setLocationAddress] = useState(""); // New state for address

  // Attendance Image (Selfie)
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);

  // Duty Images (Odometer)
  const [odoImage, setOdoImage] = useState(null);
  const [odoPreview, setOdoPreview] = useState(null);
  // const [startOdoVal, setStartOdoVal] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const [todayTrips, setTodayTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(true);
  const [nextTrip, setNextTrip] = useState(null);

  // Logic Variables
  const [attendanceList, setAttendanceList] = useState([]);
  const [isLoadingDuty, setIsLoadingDuty] = useState(true);
  const [isLocating, setIsLocating] = useState(false);
  const [dutyStatus, setDutyStatus] = useState(0);

  // Derived States for Attendance
  const hasAttendance = attendanceList.length > 0;
  const isOnline = hasAttendance && Number(attendanceList[0]?.ck_sts) === 1; // Punched In
  const isCheckedOut = hasAttendance && Number(attendanceList[0]?.ck_sts) === 2; // Punched Out

  // Dates
  // const today = new Date();
  // const currentDate = today.toISOString().split("T")[0];
  // const currentMonth = String(today.getMonth() + 1).padStart(2, "0");
  // const currentYear = today.getFullYear();
  const now = new Date();
  const istDateStr = now.toLocaleDateString("en-CA", {
    timeZone: "Asia/Kolkata",
  });
  // 3. Split parts so we can assign them to your separate variables
  const [istYear, istMonth, istDay] = istDateStr.split("-");

  // 4. Assign to your existing const names
  const currentDate = istDateStr; // e.g., "2026-01-28"
  const currentMonth = istMonth; // e.g., "01"
  const currentYear = istYear; // e.g., "2026"

  const [notes, setNotes] = useState(""); // Used for Odometer reading now
  // Checklist State: { [itemId]: { value: 1/0, image: File } }
  const [checklistData, setChecklistData] = useState({});

  // const [date, setDate] = useState(String(currentDate));
  // const [month, setMonth] = useState(currentMonth);
  // const [year, setYear] = useState(String(currentYear));
  // 5. Initialize State (Your existing logic stays the same)
  const [date, setDate] = useState(currentDate);
  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);

  // Modals / Form Visibility
  const [showAttendanceForm, setShowAttendanceForm] = useState(false);
  const [showDutyForm, setShowDutyForm] = useState(false);
  const [dutyType, setDutyType] = useState(""); // "START" or "END"

  // 1. Add these refs at the top of your component if not present
  // const watchIdRef = useRef(null);
  // const prevPosRef = useRef(null);
  // const [sessionDistance, setSessionDistance] = useState(0);

  const [profile, setProfile] = useState({
    fullName: "",
    phone: "",
    driverId: "",
    email: "",
    driver_photo: null,
  });

  const driverId = localStorage.getItem("driverRegNo");
  const [checklistItems, setChecklistItems] = useState([]);
  const fetchItemsDetails = async () => {
    try {
      const res = await api.get("/sendItems");
      const rawData = res.data?.data || [];

      const formattedList = rawData.map((item) => ({
        id: item.id,
        name: item.item_names,
        showCamera: item.photo === 1,
        photoRequired: item.is_mandatory === 1,
        // needsPhoto: [1, 2, 3].includes(Number(item.id)),
      }));

      setChecklistItems(formattedList);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Checklist");
    }
  };
  const fetchAssignedTrip = async () => {
    try {
      setLoadingTrips(true);
      const regNo = localStorage.getItem("driverRegNo");
      if (!regNo) return setTodayTrips([]);

      const res = await api.get(`/driver/assigned-trip?driver_regno=${regNo}`);

      // Normalize response like MyTripDetails does
      const tripData = Array.isArray(res.data) ? res.data : [res.data];
      // console.log("Assigned Trips:", tripData);

      // If ANY trip data exists → show it
      if (tripData && tripData.length > 0) {
        setTodayTrips(tripData);
      } else {
        setTodayTrips([]);
      }
    } catch (err) {
      console.log(err);
      setTodayTrips([]);
    } finally {
      setLoadingTrips(false);
    }
  };

  const fetchNextTrip = async () => {
    try {
      const regNo = localStorage.getItem("driverRegNo");

      const res = await api.get(`/driver/next-trip?driver_regno=${regNo}`);
      // console.log("Next Trips:", res.data);

      if (res.data.length) {
        setNextTrip(res.data[0]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // 3. Call fetchItemsDetails on load
  useEffect(() => {
    fetchItemsDetails();
  }, []);

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    fetchProfileDetails();
    fetchAttendance();
    fetchNextTrip();

    const regNo = localStorage.getItem("driverRegNo");
    if (regNo) {
      fetchAssignedTrip();
      socket.emit("join_app_session", regNo);
    } else {
      setTimeout(() => fetchAssignedTrip(), 500);
    }

    const handleNewTrip = (data) => {
      new Audio("/sounds/trip_alert.mp3").play().catch(() => {});
      toast.success(`NEW TRIP: ${data.message || "Check your dashboard"}`, {
        duration: 8000,
        position: "top-center",
      });
      fetchAssignedTrip();
    };
    socket.on("new_trip_alert", handleNewTrip);
    return () => socket.off("new_trip_alert", handleNewTrip);
  }, [token]);

  const fetchProfileDetails = async () => {
    try {
      const res = await api.get("/auth/profile");
      setProfile(res.data);
    } catch (err) {
      toast.error("Profile sync failed");
    }
  };

  const fetchAttendance = async () => {
    try {
      const dayOnly = date.includes("-") ? date.split("-")[2] : date;
      const res = await api.get(
        `/attendanceCheckDate/${driverId}/${dayOnly}/${month}/${year}`,
      );
      //  console.log("DASHBOARD DATA:", res.data?.data);
      setAttendanceList(res.data?.data || []);
    } catch {
      toast.error("Failed to load attendance");
    }
  };

  // ################## New improved 3s if  not  send  lat long ##########
  const fetchLocationName = async (latitude, longitude) => {
    if (!latitude || !longitude) return null;

    // Create a timeout promise that resolves after 5 seconds
    const timeoutPromise = new Promise((resolve) =>
      setTimeout(() => resolve("TIMEOUT"), 5000),
    );

    try {
      const apiCall = api.post("/location/geocode", {
        latitude: latitude,
        longitude: longitude,
      });

      // Race: Whichever finishes first wins
      const res = await Promise.race([apiCall, timeoutPromise]);

      if (res === "TIMEOUT") {
        console.warn("⚠️ Address fetch timed out - keeping coordinates");
        return null; // Return null so we stick with Lat/Lon
      }

      if (res.data.success) {
        return res.data.address;
      }
    } catch (err) {
      console.error("❌ Location API Error:", err);
    }
    return null;
  };

  // ################# new  function ROBUST GPS LOGIC (High -> Low Accuracy) ##############
  const getGpsLocation = (isSilent = false) => {
    if (!navigator.geolocation) return toast.error("GPS not supported");

    if (!isSilent) setIsLocating(true);
    // Shared Success Handler
    const handleSuccess = async (position) => {
      const { latitude, longitude, accuracy } = position.coords;

      // 1. Save Raw Data
      setGpsData({ latitude, longitude, accuracy });

      // 2. IMMEDIATE FALLBACK: Set coordinates as address
      // (This makes the app feel instant)
      const fallbackAddr = `Lat: ${latitude.toFixed(
        5,
      )}, Lon: ${longitude.toFixed(5)}`;
      setLocationAddress(fallbackAddr);

      // 3. Stop Spinner NOW (User can submit immediately)
      setIsLocating(false);

      // 4. Try fetching real name in background (Silent update)
      const realAddress = await fetchLocationName(latitude, longitude);
      if (realAddress) {
        setLocationAddress(realAddress); // Overwrite if successful
      }
    };

    // Attempt 1: High Accuracy (Satellite) - 5s Timeout
    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      (error) => {
        console.warn(
          "GPS High Accuracy failed. Switching to Network...",
          error,
        );

        // Attempt 2: Low Accuracy (Network/WiFi) - 10s Timeout
        navigator.geolocation.getCurrentPosition(
          handleSuccess,
          (finalError) => {
            console.error(" All Location methods failed", finalError);
            setIsLocating(false);
            if (!isSilent) {
              toast.error("Location failed. Check GPS settings.");
            }
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 },
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  // --- WARM UP GPS ON MOUNT  new ---
  useEffect(() => {
    // Only warm up if we don't have data yet
    if (!gpsData) {
      getGpsLocation(true);
    }
  }, []);

  // --- HELPER: Get Date/Time for Display (FIXED FOR IST) ---
  const getCurrentTimeDetails = () => {
    const now = new Date();

    return {
      // 1. Force Date to DD/MM/YYYY in India Time
      date: now.toLocaleDateString("en-GB", {
        timeZone: "Asia/Kolkata",
      }),

      // 2. Force Time to 10:30 AM in India Time
      time: now.toLocaleTimeString("en-US", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  const handlePunchAction = (type) => {
    // 1. Reset UI
    setImage(null);
    setPreview(null);
    setGpsData(null);
    setLocationAddress("");

    // 2. Start GPS NOW (While user opens camera)
    getGpsLocation(false);
    // 3. Open Camera (Native Input)
    setTimeout(() => {
      document.getElementById("attendanceCamera").click();
    }, 100);
  };

  const fetchDutyStatus = async () => {
    setIsLoadingDuty(true);
    const currentRegNo = localStorage.getItem("driverRegNo");

    if (!currentRegNo) {
      console.warn("No driver found ");
      setIsLoadingDuty(false);
      return;
    }

    const localDate = new Date().toLocaleDateString("en-CA");

    try {
      const res = await api.get(
        `/duty/status/${currentRegNo}?date=${localDate}`,
      );

      const statusFromApi = res.data?.dutyStatus;
      let start_odovalue = res.data?.start_odo_val || 0; // Default from main API

      const finalStatus =
        statusFromApi !== undefined && statusFromApi !== null
          ? Number(statusFromApi)
          : 0;

      setDutyStatus(finalStatus);
      // setStartOdoVal(start_odovalue);

      if (finalStatus === 1) {
        setDutyType("END");
      } else {
        setDutyType("START");
      }
    } catch (err) {
      console.error("Fetch duty status error:", err);
      setDutyStatus(0);
      setDutyType("START");
    } finally {
      setIsLoadingDuty(false);
    }
  };

  // Ensure this useEffect is present to trigger it
  useEffect(() => {
    // Only fetch if we have a valid driver identifier
    if (driverId) {
      fetchDutyStatus();
    }
  }, [driverId]);

  const handleDutyAction = (type) => {
    setDutyType(type);

    // 1. DO NOT Show Form Yet
    setShowDutyForm(true);
    setShowAttendanceForm(false);

    // 2. Reset Data
    setOdoImage(null);
    setOdoPreview(null);
    setNotes("");
    setChecklistData({});

    getGpsLocation(false);

    // 3. Open Camera
    // setTimeout(() => {
    //   if (odoCameraRef.current) odoCameraRef.current.click();
    // }, 200);
  };

  // --- CAPTURE: UI Only After Photo ---
  const handleSelfieCapture = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const toastId = toast.loading("Processing Selfie...");
      try {
        const compressed = await compressImage(file);
        setImage(compressed);
        setPreview(URL.createObjectURL(compressed));

        // 4. NOW show the preview layout
        setShowAttendanceForm(true);

        toast.dismiss(toastId);
      } catch (err) {
        toast.dismiss(toastId);
        toast.error("Error processing image");
      }
    }
  };

  const handleOdoCapture = async (e) => {
    const file = e.target.files[0];

    if (file) {
      const toastId = toast.loading("Processing Odometer...");
      try {
        const compressed = await compressImage(file);
        setOdoImage(compressed);
        setOdoPreview(URL.createObjectURL(compressed));

        // 4. NOW Show the Form (Because photo exists)
        setShowDutyForm(true);

        toast.dismiss(toastId);
      } catch (err) {
        toast.dismiss(toastId);
        toast.error("Error processing image");
      }
    } else {
      // User Cancelled Camera: Do nothing.
      // Form remains hidden.
    }
  };

  const handleChecklistPhoto = async (id, e) => {
    const file = e.target.files[0];
    if (file) {
      const toastId = toast.loading("Compressing...");
      try {
        const compressed = await compressImage(file);
        setChecklistData((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            image: compressed,
            imagePreview: URL.createObjectURL(compressed),
          },
        }));
        toast.dismiss(toastId);
      } catch (err) {
        toast.dismiss(toastId);
        toast.error("Error processing image");
      }
    }
  };

  const handleChecklistValue = (id, val) => {
    setChecklistData((prev) => ({
      ...prev,
      [id]: { ...prev[id], value: val },
    }));
  };

  // --- SUBMIT FUNCTIONS (PUNCH IN/OUT) ---
  const handleAttendanceSubmit = async () => {
    // A. Validation with gentle haptic style feedback
    if (!image) {
      return toast.error("📸 Please take a selfie to continue");
    }

    // B. GPS Wait Lock – driver friendly messages
    if (!gpsData) {
      if (isLocating) {
        return toast.info("📍 Fetching accurate location, please wait...");
      } else {
        getGpsLocation();
        return toast.warning("📡 Location missing – retrying GPS...");
      }
    }

    setSubmitting(true);

    try {
      const formData = new FormData();

      formData.append("selfie", image);
      formData.append("latitude", gpsData.latitude);
      formData.append("longitude", gpsData.longitude);
      // If address is missing or "Unknown", send coordinates
      let finalLocation = locationAddress;
      if (!finalLocation || finalLocation === "Unknown Location") {
        finalLocation = `Lat: ${gpsData.latitude.toFixed(
          5,
        )}, Lon: ${gpsData.longitude.toFixed(5)}`;
      }
      formData.append("locationName", finalLocation);

      // formData.append("locationName", locationAddress || "Unknown");
      formData.append("accuracy", gpsData.accuracy);

      const btnParam = !attendanceList?.length
        ? 0
        : isOnline
        ? attendanceList[0]?.drv_atid
        : 0;

      formData.append("btnParameter", btnParam);

      const res = await api.post("/attendance", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // D. Success dialog – looks like Android material card
      await Swal.fire({
        icon: "success",
        title: "✅ Attendance Marked",
        text: res.data.message,
        background: "#ecfeff",
        timer: 1500,
        showConfirmButton: false,
        width: "280px",
        padding: "1.25rem 1rem",
        customClass: {
          popup: "native-android-alert rounded-4 shadow-lg border-0",
          icon: "m-0 mx-auto",
          title: "fs-6 fw-bold mt-3 mb-1",
          htmlContainer: "m-0 small text-muted",
        },
        backdrop: "rgba(0,0,0,0.6)",
      });

      // E. Smooth state update
      setShowAttendanceForm(false);
      setImage(null);
      setPreview(null);
      fetchAttendance();

      setSubmitting(false);
    } catch (err) {
      setSubmitting(false);

      // F. Error dialog – bold but friendly
      Swal.fire({
        icon: "error",
        title: "⚠ Punch Failed",
        html: `
          <div style="font-size:14px">
            ${
              err.response?.data?.message ||
              "Connection error. Please try again."
            }
          </div>
        `,
        background: "#fff1f2",
        confirmButtonText: "Retry",
        confirmButtonColor: "#dc2626",
        showClass: {
          popup: "swal2-animate-shake",
        },
        width: "280px",
        padding: "1.25rem 1rem 1rem",
        customClass: {
          popup: "rounded-4 shadow-lg border-0",
          icon: "m-0 mx-auto",
          title: "fs-6 fw-bold mt-3 mb-1",
          htmlContainer: "m-0 mb-3 text-muted",
          actions: "w-100 m-0 p-0",
          confirmButton: "w-100 rounded-pill py-2 m-0 fw-bold",
        },
        backdrop: "rgba(0,0,0,0.6)",
      });
    }
  };

  // #########  new  location name and gps data ############
  const handleDutySubmit = async () => {
    if (!odoImage) return toast.error("📸 Odometer photo required");
    if (!notes) return toast.error("⌨ Enter odometer reading");

    if (!gpsData) {
      getGpsLocation(false);
      return toast.warning("📍 Waiting for GPS location...");
    }

    for (let item of checklistItems) {
      const data = checklistData[item.id];
      if (data?.value === undefined)
        return toast.warning(`Please verify: ${item.name}`);
      if (item.showCamera && item.photoRequired) {
        if (!data?.image) {
          return toast.error(`📸 Photo proof required for: "${item.name}"`);
        }
      }
    }

    setSubmitting(true);
    try {
      const buttonParameter = dutyType === "START" ? 1 : 2;
      const formData = new FormData();
      formData.append("odometer_image", odoImage);
      formData.append("odometerValue", notes);

      formData.append("latitude", gpsData.latitude);
      formData.append("longitude", gpsData.longitude);
      formData.append(
        "locationName",
        locationAddress || `Lat: ${gpsData.latitude.toFixed(5)}`,
      );

      const checklistPayload = {};
      checklistItems.forEach((item) => {
        const data = checklistData[item.id];
        checklistPayload[item.id] = { name: item.name, value: data.value };
        if (data?.image) {
          formData.append(`checklist_image_${item.id}`, data.image);
          checklistPayload[item.id].image = data.image.name;
        }
      });

      formData.append("ci_itemchkstatus", JSON.stringify(checklistPayload));
      formData.append("buttonParameter", buttonParameter);

      const res = await api.post("/duty/duty", formData);

      await Swal.fire({
        icon: "success",
        title: buttonParameter === 1 ? "🟢 Duty Started" : "🔴 Duty Ended",
        text: res.data.message,
        timer: 1700,
        showConfirmButton: false,
        width: "280px",
        padding: "1.25rem 1rem",
        customClass: {
          popup: "rounded-4 shadow-lg border-0",
          icon: "m-0 mx-auto",
          title: "fs-6 fw-bold mt-3 mb-1",
          htmlContainer: "m-0 small text-muted",
        },
        backdrop: "rgba(0,0,0,0.6)",
      });

      setDutyStatus(buttonParameter);
      setShowDutyForm(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Sync Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const styles = {
    mainWrapper: {
      minHeight: "100dvh",
      background: "#ffffffff",
      display: "flex",
      justifyContent: "center",
    },
    appContainer: {
      width: "100%",
      maxWidth: "480px",
      background: "#ffffffff",
      minHeight: "100dvh",
      position: "relative",
    },
    nativeCard: {
      backgroundColor: "#ffffff",
      borderRadius: "16px", // Rounded corners like modern Android
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)", // Soft elevation
      overflow: "hidden",
    },
    profileImg: {
      width: "60px",
      height: "60px",
      borderRadius: "50%",
      objectFit: "cover",
      border: "2px solid #ff6600ff",
      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    },
    profileInitial: {
      width: "56px",
      height: "56px",
      borderRadius: "50%",
      backgroundColor: "#e0f7fa",
      color: "#006064",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: "bold",
      fontSize: "22px",
      border: "2px solid #fff",
      boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    },
    statBox: {
      backgroundColor: "#f8f9fa",
      borderRadius: "12px",
      padding: "12px",
      height: "100%",
      position: "relative",
      border: "1px solid rgba(0,0,0,0.03)",
    },
    label: {
      fontSize: "0.90rem",
      color: "#0059ffff",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      fontWeight: "600",
      marginBottom: "4px",
    },

    pill: {
      fontSize: "0.85rem",
      fontWeight: "700",
      padding: "2px 8px",
      borderRadius: "10px",
      display: "inline-block",
    },
    stickyFooter: {
      position: "fixed",
      bottom: "64px",
      left: "50%",
      transform: "translateX(-50%)",

      width: "100%",
      maxWidth: "420px",

      background: "#ffffffff",
      padding: "8px",

      borderRadius: "30px",

      zIndex: 1000,
    },
    btnGradient: {
      width: "100%",
      height: "40px",

      background: "linear-gradient(135deg, #2563eb, #1e40af)",
      border: "none",
      color: "#ffffff",

      borderRadius: "999px",
      fontSize: "16px",
      fontWeight: "600",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
    },
    btnDanger: {
      width: "100%",
      height: "40px",

      background: "linear-gradient(135deg, #ef4444, #b91c1c)",
      border: "none",
      color: "#ffffff",

      borderRadius: "999px",
      fontSize: "16px",
      fontWeight: "600",

      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
    },
    uploadBox: {
      background: "#f8fafc",
      border: "2px dashed #cbd5e1",
      borderRadius: "12px",
      height: "150px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      overflow: "hidden",
      position: "relative",
    },
    imgPreview: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
  };

  if (isLoadingDuty) {
    return (
      <div className="loader-container">
        <Activity />
        <style>{`
            .loader-container {
                position: fixed; 
                top: 0;
                left: 0;
                width: 100%;
                height: 100%; 
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                background: #ffffff;
            }
        `}</style>
      </div>
    );
  }

  return (
    <div style={styles.mainWrapper}>
      <div style={styles.appContainer}>
        {/* =========================================
            1. HEADER SECTION (Profile)
           ========================================= */}
        <div
          className="position-relative overflow-hidden shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f7823e 0%, #3730a3 100%)",
            borderBottomLeftRadius: "28px",
            borderBottomRightRadius: "28px",
            padding: "20px 20px 20px",
            marginBottom: "20px",
            color: "white",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "-20px",
              right: "-20px",
              width: "100px",
              height: "100px",
              background: "rgba(255,255,255,0.1)",
              borderRadius: "50%",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "-10px",
              left: "10%",
              width: "60px",
              height: "60px",
              background: "rgba(255,255,255,0.05)",
              borderRadius: "50%",
            }}
          />

          <div className="d-flex align-items-center position-relative z-1">
            {/* Profile Image with Ring */}
            <div className="me-3 position-relative">
              <div
                className="rounded-circle p-1 bg-white bg-opacity-25"
                style={{ display: "inline-block" }}
              >
                {profile.driver_photo ? (
                  <img
                    src={`${IMAGE_BASE_URL}${profile.driver_photo}`}
                    alt="Driver"
                    className="rounded-circle shadow-sm"
                    style={{
                      width: "60px",
                      height: "60px",
                      objectFit: "cover",
                      border: "2px solid white",
                    }}
                  />
                ) : (
                  <div
                    className="rounded-circle bg-white text-primary d-flex align-items-center justify-content-center fw-bold shadow-sm"
                    style={{
                      width: "58px",
                      height: "58px",
                      fontSize: "1.4rem",
                      border: "2px solid white",
                    }}
                  >
                    {profile.fullName
                      ? profile.fullName.charAt(0).toUpperCase()
                      : "D"}
                  </div>
                )}
              </div>

              {/* Pulsing Online Status Dot */}
              <span className="position-absolute bottom-0 end-0 p-1">
                <span
                  className={`d-block rounded-circle border border-2 border-white ${
                    isOnline ? "bg-success" : "bg-secondary"
                  }`}
                  style={{
                    width: "14px",
                    height: "14px",
                    boxShadow: isOnline
                      ? "0 0 0 2px rgba(34, 197, 94, 0.4)"
                      : "none",
                  }}
                ></span>
              </span>
            </div>

            {/* Text Content */}
            <div className="flex-grow-1 overflow-hidden">
              <span
                className="d-block mb-1 text-white text-opacity-65 small fw-medium text-uppercase"
                style={{ letterSpacing: "0.5px", fontSize: "0.75rem" }}
              >
                Welcome back
              </span>
              <h4
                className="mb-1 fw-bold text-white text-truncate"
                style={{ letterSpacing: "-0.3px", fontSize: "1.25rem" }}
              >
                {profile.fullName || "Driver"}
              </h4>

              {/* Date Pill */}
              <div
                className="d-inline-flex align-items-center bg-white bg-opacity-10 rounded-pill px-2 py-1 mt-1"
                style={{ backdropFilter: "blur(4px)" }}
              >
                <Calendar
                  size={12}
                  className="me-1 text-white text-opacity-75"
                />
                <span
                  className="text-white text-opacity-90 small fw-medium"
                  style={{ fontSize: "0.75rem" }}
                >
                  {getCurrentTimeDetails().date}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* =========================================
            2. ACTION BUTTONS GRID (Native Look)
           ========================================= */}
        <div className="px-2 mb-3">
          <div className="row g-2">
            {/* A. PUNCH IN (Attendance) - Only show if NOT Punched In */}
            {!hasAttendance && !showAttendanceForm && (
              <div className="col-12 animate__animated animate__fadeIn">
                <button
                  className="btn w-100 py-2 rounded-4 shadow-sm d-flex align-items-center justify-content-between px-4 border-0"
                  style={{
                    background:
                      "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                    color: "white",
                  }}
                  onClick={() => handlePunchAction("IN")}
                >
                  <div className="d-flex align-items-center">
                    <div className="bg-white bg-opacity-25 rounded-circle p-2 me-3">
                      <Fingerprint size={20} />
                    </div>
                    <div className="text-start">
                      <div className="fw-bold" style={{ fontSize: "1rem" }}>
                        PUNCH IN
                      </div>
                      <div
                        className="small opacity-75"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Submit Attendance
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="opacity-50" />
                </button>
              </div>
            )}

            {/* B. START DUTY - Show if Punched In AND No Active Duty */}
            {!isLoadingDuty && isOnline && dutyStatus === 0 && (
              <div className="col-12 animate__animated animate__fadeIn">
                <button
                  className="btn w-100 py-2 rounded-4 shadow-sm d-flex align-items-center justify-content-between px-4 border-0"
                  style={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "white",
                  }}
                  onClick={() => handleDutyAction("START")}
                >
                  <div className="d-flex align-items-center">
                    <div className="bg-white bg-opacity-25 rounded-circle p-2 me-3">
                      <Car size={20} />
                    </div>
                    <div className="text-start">
                      <div className="fw-bold" style={{ fontSize: "1rem" }}>
                        START DUTY
                      </div>
                      <div
                        className="small opacity-75"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Begin your today shift
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="opacity-50" />
                </button>
              </div>
            )}

            {/* C. END DUTY - Show if Duty Started (1) */}
            {!isLoadingDuty && dutyStatus === 1 && !showDutyForm && (
              <div className="col-12 animate__animated animate__fadeIn">
                <button
                  className="btn w-100 py-2 rounded-4 shadow-sm d-flex align-items-center justify-content-between px-4 border-0"
                  style={{
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    color: "white",
                  }}
                  onClick={() => handleDutyAction("END")}
                >
                  <div className="d-flex align-items-center">
                    <div className="bg-white bg-opacity-25 rounded-circle p-2 me-3">
                      <AlertCircle size={20} />
                    </div>
                    <div className="text-start">
                      <div className="fw-bold" style={{ fontSize: "1rem" }}>
                        END DUTY
                      </div>
                      <div
                        className="small opacity-75"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Complete your today shift
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="opacity-50" />
                </button>
              </div>
            )}

            {/* D. PUNCH OUT - Show if Duty Ended (2) OR (0 but punched in) */}
            {isOnline &&
              dutyStatus === 2 &&
              hasAttendance &&
              !showAttendanceForm && (
                <div className="col-12 animate__animated animate__fadeIn mt-2">
                  <button
                    className="btn w-100 py-2 rounded-4 shadow-sm d-flex align-items-center justify-content-between px-4 border-0"
                    style={{
                      background:
                        "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      color: "white",
                    }}
                    onClick={() => handlePunchAction("OUT")}
                  >
                    <div className="d-flex align-items-center">
                      <div className="bg-white bg-opacity-25 rounded-circle p-2 me-3">
                        <LogOut size={20} />
                      </div>
                      <div className="text-start">
                        <div className="fw-bold" style={{ fontSize: "1rem" }}>
                          PUNCH OUT
                        </div>
                        <div
                          className="small opacity-75"
                          style={{ fontSize: "0.75rem" }}
                        >
                          Submit your Attendance
                        </div>
                      </div>
                    </div>
                    <ChevronRight size={20} className="opacity-50" />
                  </button>
                </div>
              )}
          </div>
        </div>

        {/* =========================================
            3. FORMS AREA (Checklist / Attendance)
            Renders directly below buttons when active
           ========================================= */}

        {/* --- DUTY CHECKLIST FORM --- */}
        {showDutyForm && (
          <div className="px-3 mb-4 animate__animated animate__fadeInUp">
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="card-header bg-primary text-white border-0 py-3 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold d-flex align-items-center">
                  {dutyType === "START" ? (
                    <Car className="me-2" size={18} />
                  ) : (
                    <AlertCircle className="me-2" size={18} />
                  )}
                  {dutyType === "START"
                    ? "Start Duty Checklist"
                    : "End Duty Checklist"}
                </h6>
                <button
                  className="btn btn-sm btn-light rounded-circle p-1"
                  onClick={() => setShowDutyForm(false)}
                >
                  <XCircle size={18} className="text-dark" />
                </button>
              </div>

              <div className="card-body p-3 bg-light">
                {/* 1. Odometer */}
                <div className="bg-white p-3 rounded-3 shadow-sm mb-3">
                  <label className="fw-bold small text-muted mb-2 text-uppercase">
                    1. Odometer Reading
                  </label>
                  <div
                    style={styles.uploadBox}
                    className="mb-3"
                    onClick={() => document.getElementById("odoCamera").click()}
                  >
                    {odoPreview ? (
                      <img
                        src={odoPreview}
                        alt="odo"
                        style={styles.imgPreview}
                      />
                    ) : (
                      <div className="text-center text-secondary py-3">
                        <Camera size={28} className="mb-1 text-primary" />
                        <div className="small fw-bold text-primary">
                          Capture Odometer
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="tel" // Forces numeric keypad on most phones
                    inputMode="numeric" // Ensures number pad on iOS/Android
                    maxLength={6} // Native limit
                    className="form-control form-control-lg bg-light border-0 fw-bold text-center"
                    placeholder="Enter KM Reading"
                    value={notes}
                    onChange={(e) => {
                      // Allow only numbers and limit to 6 digits
                      const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setNotes(val);
                    }}
                  />
                </div>

                {/* 2. Checklist */}
                <div className="bg-white p-3 rounded-3 shadow-sm mb-3">
                  <label className="fw-bold small text-muted mb-3 text-uppercase border-bottom w-100 d-block pb-2">
                    2. Vehicle Checklist
                  </label>
                  {checklistItems.map((item) => (
                    <div
                      key={item.id}
                      className="mb-3 pb-3 border-bottom border-light last:border-0"
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span
                          className="small fw-bold text-dark"
                          style={{ maxWidth: "60%" }}
                        >
                          {item.name}
                        </span>
                        <div className="btn-group btn-group-sm">
                          <button
                            className={`btn ${
                              checklistData[item.id]?.value === 1
                                ? "btn-success"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => handleChecklistValue(item.id, 1)}
                          >
                            Yes
                          </button>
                          <button
                            className={`btn ${
                              checklistData[item.id]?.value === 0
                                ? "btn-danger"
                                : "btn-outline-secondary"
                            }`}
                            onClick={() => handleChecklistValue(item.id, 0)}
                          >
                            No
                          </button>
                        </div>
                      </div>

                      {item.showCamera &&
                        checklistData[item.id]?.value !== undefined && (
                          <div className="mt-2 animate__animated animate__fadeIn">
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              data-camera="back"
                              hidden
                              id={`check_img_${item.id}`}
                              onChange={(e) => handleChecklistPhoto(item.id, e)}
                            />
                            <button
                              className={`btn btn-sm w-100 d-flex align-items-center justify-content-center ${
                                checklistData[item.id]?.image
                                  ? "btn-success text-white border-0"
                                  : item.photoRequired
                                  ? "btn-outline-danger"
                                  : "btn-light border text-primary"
                              }`}
                              onClick={() =>
                                document
                                  .getElementById(`check_img_${item.id}`)
                                  .click()
                              }
                            >
                              {checklistData[item.id]?.image ? (
                                <>
                                  <CheckCircle2 size={14} className="me-2" />{" "}
                                  Photo Added
                                </>
                              ) : (
                                <>
                                  <Camera size={14} className="me-2" />{" "}
                                  {item.photoRequired
                                    ? "Upload Photo"
                                    : "Upload Photo"}
                                </>
                              )}
                            </button>
                            {checklistData[item.id]?.imagePreview && (
                              <div
                                className="mt-2 rounded-3 overflow-hidden border shadow-sm"
                                style={{ height: "80px", width: "80px" }}
                              >
                                <img
                                  src={checklistData[item.id]?.imagePreview}
                                  className="w-100 h-100 object-fit-cover"
                                  alt="Preview"
                                />
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  ))}
                </div>

                {/* Submit Button */}
                <button
                  className={`btn w-100 py-3 rounded-4 fw-bold shadow ${
                    dutyType === "START" ? "btn-primary" : "btn-danger"
                  }`}
                  onClick={handleDutySubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="animate-spin" />
                  ) : dutyType === "START" ? (
                    "CONFIRM START DUTY"
                  ) : (
                    "CONFIRM END DUTY"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- ATTENDANCE FORM (Punch In/Out Preview) --- */}
        {showAttendanceForm && preview && (
          <div className="px-3 mb-4 animate__animated animate__fadeInUp">
            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
              <div className="position-relative">
                <img
                  src={preview}
                  style={{ width: "100%", height: "250px", objectFit: "cover" }}
                  alt="Selfie"
                />
                <div
                  className="position-absolute bottom-0 start-0 w-100 p-3 bg-gradient-to-t from-black to-transparent"
                  style={{
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
                  }}
                >
                  <div className="text-white small d-flex align-items-center">
                    <MapPin size={14} className="me-1" />{" "}
                    {locationAddress || "Locating..."}
                  </div>
                  <div className="text-white fw-bold mt-1">
                    {getCurrentTimeDetails().date} •{" "}
                    {getCurrentTimeDetails().time}
                  </div>
                </div>
              </div>
              <div className="card-body p-3 d-flex gap-2">
                <button
                  className="btn btn-light flex-grow-1 fw-bold text-secondary"
                  onClick={() => {
                    setShowAttendanceForm(false);
                    setPreview(null);
                    setImage(null);
                  }}
                >
                  Retake
                </button>
                <button
                  className="btn btn-primary flex-grow-1 fw-bold text-white"
                  onClick={handleAttendanceSubmit}
                  disabled={submitting || !locationAddress}
                >
                  {submitting ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    "CONFIRM"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* =========================================
            X. QUICK ACTIONS GRID (Horizontal Slider)
           ========================================= */}
        <div className="px-2 mb-3 animate__animated animate__fadeIn">
          {/* ── Unified Header (Matches Today's Trips exactly) ── */}
          <div className="d-flex align-items-center justify-content-between mb-2 px-2">
            <div>
              <h6
                className="fw-bold mb-0 text-dark"
                style={{ fontSize: "1rem", letterSpacing: "-0.2px" }}
              >
                Quick Actions
              </h6>
              <p
                className="text-secondary mb-0 fw-medium mt-1"
                style={{ fontSize: "0.75rem" }}
              >
                Fast access utilities
              </p>
            </div>
          </div>

          {/* ── Slider Card ── */}
          <div
            className="card border-0 bg-white overflow-hidden"
            style={{
              borderRadius: "20px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div className="card-body px-2 py-2">
              {/* Swipeable Container */}
              <div className="qa-scroll-container px-2">
                {/* 1. Add Fuel */}
                <div
                  className="quick-action-item"
                  onClick={() => navigate("/refuel")}
                >
                  <div className="qa-icon-circle bg-warning bg-opacity-10">
                    <Fuel size={24} className="text-warning" strokeWidth={2} />
                  </div>
                  <span className="qa-action-label">
                    Add
                    <br />
                    Fuel
                  </span>
                </div>

                {/* 2. Expenses */}
                <div
                  className="quick-action-item"
                  onClick={() => navigate("/other-expense")}
                >
                  <div className="qa-icon-circle bg-info bg-opacity-10">
                    <Receipt size={24} className="text-info" strokeWidth={2} />
                  </div>
                  <span className="qa-action-label">
                    Add
                    <br />
                    Expense
                  </span>
                </div>

                {/* 3. Generate DMR */}
                <div
                  className="quick-action-item"
                  onClick={() => navigate("/dmr")}
                >
                  <div className="qa-icon-circle bg-primary bg-opacity-10">
                    <FileText
                      size={24}
                      className="text-primary"
                      strokeWidth={2}
                    />
                  </div>
                  <span className="qa-action-label">
                    Generate
                    <br />
                    DMR
                  </span>
                </div>

                {/* 4. My Attendance */}
                <div
                  className="quick-action-item"
                  onClick={() => navigate("/attendance-history")}
                >
                  <div className="qa-icon-circle bg-success bg-opacity-10">
                    <UserCheck
                      size={24}
                      className="text-success"
                      strokeWidth={2}
                    />
                  </div>
                  <span className="qa-action-label">
                    My
                    <br />
                    Attendance
                  </span>
                </div>

                {/* 5. My Documents */}
                <div
                  className="quick-action-item"
                  onClick={() => navigate("/documents")}
                >
                  <div
                    className="qa-icon-circle"
                    style={{ backgroundColor: "rgba(99, 102, 241, 0.1)" }}
                  >
                    <FolderOpen size={24} color="#6366f1" strokeWidth={2} />
                  </div>
                  <span className="qa-action-label">
                    My
                    <br />
                    Documents
                  </span>
                </div>

                {/* 6. Contact Office */}
                <div
                  className="quick-action-item"
                  onClick={() => navigate("/contact-office")}
                >
                  <div className="qa-icon-circle bg-danger bg-opacity-10">
                    <PhoneCall
                      size={24}
                      className="text-danger"
                      strokeWidth={2}
                    />
                  </div>
                  <span className="qa-action-label">
                    Contact
                    <br />
                    Office
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* ========================================= */}

        {/* =========================================
            4. TRIPS SECTION (List)
           ========================================= */}
        <div className="px-2">
          {/* ── Header ──────────────────────────────────────────────────────────── */}
          <div className="d-flex align-items-center justify-content-between mb-3 px-2">
            <div>
              <h6
                className="fw-bold mb-0 text-dark"
                style={{ fontSize: "1rem", letterSpacing: "-0.2px" }}
              >
                Today's Trips
              </h6>
              <p
                className="text-secondary mb-0 fw-medium mt-1"
                style={{ fontSize: "0.75rem" }}
              >
                Your assigned schedule
              </p>
            </div>

            <span
              className="badge rounded-pill text-white fw-bold px-3 py-1 shadow-sm"
              style={{
                background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                fontSize: "0.68rem",
                letterSpacing: "0.5px",
              }}
            >
              {todayTrips.length} ACTIVE
            </span>
          </div>

          {/* ################# Next Trip Blink ########## */}
          {nextTrip && (
            <div className="ticket-alert-card smooth-blink text-center p-3 mx-3 mt-3">
              <span style={{ fontSize: "1.2rem" }}>🚖</span>
              <span
                className="fw-semibold ms-2"
                style={{ color: "#ffffff", fontSize: "1.1rem" }}
              >
                Your Next Trip (<b>{nextTrip.confirmation_id}</b>)
              </span>
              <br className="d-block d-sm-none" />{" "}
              {/* Forces a line break on small phone screens */}
              {/* Using a margin-top (mt-2) and soft white text for the second part */}
              <div
                className="mt-2"
                style={{
                  fontSize: "0.95rem",
                  color: "rgba(255, 255, 255, 0.85)",
                }}
              >
                Begins On{" "}
                <b style={{ color: "#ffffff" }}>
                  {new Date(nextTrip.Journey_date).toLocaleDateString("en-GB")}
                </b>{" "}
                At{" "}
                <b style={{ color: "#ffffff" }}>{nextTrip.arrive_time_name}</b>
              </div>
            </div>
          )}
          {/* ####################### Blink end ############### */}

          {/* ── Trip List ───────────────────────────────────────────────────────── */}
          <div className="trip-list pb-3">
            {loadingTrips ? (
              <div
                className="d-flex flex-column justify-content-center align-items-center"
                style={{ height: "50%" }}
              >
                <TripLoader />
              </div>
            ) : todayTrips.length > 0 ? (
              todayTrips.map((trip) => (
                <div
                  key={trip.enq_id}
                  onClick={() => {
                    if (!isOnline || Number(dutyStatus) !== 1) {
                      Swal.fire({
                        icon: "warning",
                        title: "Restricted Access",
                        text: "Please Start Duty to view trip details.",
                        confirmButtonColor: "#6366f1",
                        confirmButtonText: "Got it",
                        width: "280px",
                        padding: "1.25rem 1rem 1rem",
                        customClass: {
                          popup: "rounded-4 shadow-lg border-0",
                          icon: "m-0 mx-auto",
                          title: "fs-6 fw-bold mt-3 mb-1",
                          htmlContainer: "m-0 mb-3 small text-muted",
                          actions: "w-100 m-0 p-0",
                          confirmButton: "w-100 rounded-pill py-2 m-0 fw-bold",
                        },
                        backdrop: "rgba(0,0,0,0.6)",
                      });
                      return;
                    }
                    navigate(`/my-trips/${trip.enq_id}`);
                  }}
                  className="card border-0 rounded-4 mb-3 overflow-hidden shadow-sm"
                  style={{
                    opacity: !isOnline || Number(dutyStatus) !== 1 ? 0.65 : 1,
                    cursor: "pointer",
                    borderLeft: "4px solid transparent",
                    background:
                      "linear-gradient(#fff,#fff) padding-box, linear-gradient(180deg,#22c55e,#ef4444) border-box",
                    borderLeftWidth: "4px",
                    borderLeftStyle: "solid",
                    transition: "transform 0.18s ease, box-shadow 0.18s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 12px 28px rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <div className="card-body p-0">
                    {/* ── Top Row: Time + Status ───────────────────────────────── */}
                    <div className="d-flex justify-content-between align-items-start px-3 pt-3 pb-2 border-bottom">
                      <div>
                        <div
                          className="fw-black text-dark"
                          style={{
                            fontSize: "2rem",
                            letterSpacing: "-1.5px",
                            lineHeight: 1,
                            fontWeight: 800,
                          }}
                        >
                          {trip.arrive_time_name || "--:--"}
                        </div>
                        <div
                          className="text-uppercase text-secondary fw-semibold mt-1"
                          style={{ fontSize: "0.62rem", letterSpacing: "1px" }}
                        >
                          Pick Up Time
                        </div>
                      </div>
                      <div className="text-end">
                        <span
                          className="badge bg-success bg-opacity-10 text-success fw-bold rounded-2 px-2 py-1"
                          style={{
                            fontSize: "0.62rem",
                            letterSpacing: "0.5px",
                          }}
                        >
                          SCHEDULED
                        </span>
                        <div
                          className="text-primary font-monospace mt-1"
                          style={{ fontSize: "0.92rem" }}
                        >
                          #{trip.confirmation_id}
                        </div>
                      </div>
                    </div>

                    {/* ── Route Timeline ──────────────────────────────────────── */}
                    <div className="px-3 py-3 position-relative">
                      {/* Vertical line */}
                      <div
                        className="position-absolute"
                        style={{
                          left: "27px",
                          top: "28px",
                          bottom: "28px",
                          width: "1.5px",
                          background: "linear-gradient(180deg,#22c55e,#ef4444)",
                          opacity: 0.3,
                          zIndex: 0,
                        }}
                      />

                      {/* Pickup */}
                      <div
                        className="d-flex align-items-center mb-3 position-relative"
                        style={{ zIndex: 1 }}
                      >
                        <div
                          className="bg-white rounded-circle border border-2 border-success d-flex align-items-center justify-content-center me-3 shadow-sm flex-shrink-0"
                          style={{ width: "22px", height: "22px" }}
                        >
                          <div
                            className="bg-success rounded-circle"
                            style={{ width: "7px", height: "7px" }}
                          />
                        </div>
                        <div className="overflow-hidden">
                          <div
                            className="fw-semibold text-dark text-truncate"
                            style={{ fontSize: "0.9rem" }}
                          >
                            {trip.pickup_location || "Pickup Point"}
                          </div>
                          <div
                            className="text-secondary"
                            style={{ fontSize: "0.68rem", fontWeight: 500 }}
                          >
                            Pickup Location
                          </div>
                        </div>
                      </div>

                      {/* Dropoff */}
                      <div
                        className="d-flex align-items-center position-relative"
                        style={{ zIndex: 1 }}
                      >
                        <div
                          className="bg-white rounded-circle border border-2 border-danger d-flex align-items-center justify-content-center me-3 shadow-sm flex-shrink-0"
                          style={{ width: "22px", height: "22px" }}
                        >
                          <div
                            className="bg-danger rounded-1"
                            style={{ width: "7px", height: "7px" }}
                          />
                        </div>
                        <div className="overflow-hidden">
                          <div
                            className="fw-semibold text-dark text-truncate"
                            style={{ fontSize: "0.9rem" }}
                          >
                            {trip.dropoff_location || "Destination"}
                          </div>
                          <div
                            className="text-secondary"
                            style={{ fontSize: "0.68rem", fontWeight: 500 }}
                          >
                            Drop Location
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Footer: Guest + View ────────────────────────────────── */}
                    <div className="d-flex align-items-center justify-content-between px-3 pb-3 pt-2 bg-light border-top">
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: "34px",
                            height: "34px",
                            background:
                              "linear-gradient(135deg,#ede9fe,#ddd6fe)",
                          }}
                        >
                          <User size={14} color="#7c3aed" />
                        </div>
                        <span
                          className="fw-semibold text-dark text-truncate"
                          style={{ fontSize: "0.83rem", maxWidth: "140px" }}
                        >
                          {trip.customer_name || "Guest"}
                        </span>
                      </div>

                      <button
                        className="btn btn-sm fw-bold d-flex align-items-center gap-1 text-white border-0 rounded-3 px-3 py-2"
                        style={{
                          background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                          fontSize: "0.75rem",
                          boxShadow: "0 4px 10px rgba(99,102,241,0.3)",
                        }}
                      >
                        View <ChevronRight size={13} strokeWidth={3} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              /* ── Empty State ──────────────────────────────────────────────────── */
              <div className="text-center py-4 bg-white rounded-4 border mx-2">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: "64px",
                    height: "64px",
                    background: "linear-gradient(135deg,#f0f0ff,#ede9fe)",
                  }}
                >
                  <ClipboardList size={28} color="#7c3aed" />
                </div>
                <h6 className="fw-bold text-dark mb-1">No Trips Assigned</h6>
                <p className="text-secondary small mb-0">
                  Enjoy your break! 🎉
                </p>
              </div>
            )}
          </div>

          <style>{`
        .trip-list .card:active { transform: scale(0.975) !important; opacity: 0.88 !important; }
        .qa-scroll-container {
      display: flex;
      flex-wrap: nowrap;
      overflow-x: auto;
      gap: 12px; /* Space between each item */
      padding-bottom: 5px;
      -webkit-overflow-scrolling: touch; /* Smooth momentum scrolling on iOS */
      
      /* Hide scrollbar for a clean native look */
      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE and Edge */
    }
    
    .qa-scroll-container::-webkit-scrollbar {
      display: none; /* Chrome, Safari and Opera */
    }

    /* QUICK ACTION ITEMS */
    .quick-action-item {
      flex: 0 0 auto; /* Prevents items from shrinking */
      width: 76px; /* Fixed width for uniform sizing */
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      padding: 8px 4px;
      border-radius: 16px; 
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .quick-action-item:active {
      transform: scale(0.92); 
      background-color: #f1f5f9; 
    }

    .qa-icon-circle {
      width: 48px;  
      height: 48px;
      border-radius: 50%; 
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 8px;
    }

    .qa-action-label {
      color: #334155;
      font-weight: 600;
      font-size: 0.72rem;
      line-height: 1.2;
      text-align: center;
      letter-spacing: -0.1px;
    }
        .ticket-alert-card {
          background: linear-gradient(135deg, #6c52af 0%, #c881f2 100%);
          border-radius: 16px;
          margin-bottom:12px;
          position: relative;
          
          -webkit-mask-image: radial-gradient(circle at 75% 0%, transparent 12px, black 13px),
                              radial-gradient(circle at 75% 100%, transparent 12px, black 13px);
          -webkit-mask-size: 100% 51%, 100% 51%;
          -webkit-mask-position: top, bottom;
          -webkit-mask-repeat: no-repeat;
          
          mask-image: radial-gradient(circle at 75% 0%, transparent 12px, black 13px),
                      radial-gradient(circle at 75% 100%, transparent 12px, black 13px);
          mask-size: 100% 51%, 100% 51%;
          mask-position: top, bottom;
          mask-repeat: no-repeat;
          
          filter: drop-shadow(0px 8px 16px rgba(108, 82, 175, 0.3));
        }

        .smooth-blink {
          animation: ticketFade 1.2s infinite ease-in-out;
          will-change: opacity;
        }

        @keyframes ticketFade {
          0% { opacity: 1; }
          50% { opacity: 0.3; }
          100% { opacity: 1; }
        }
  `}</style>
        </div>

        <div className="px-2">
          <img
            src={Banner}
            alt="Dashboard Banner"
            className="w-100 rounded-4"
            style={{
              objectFit: "cover",
              height: "auto",
              display: "block",
            }}
          />
        </div>
      </div>

      {/* Hidden Inputs (Keep as is) */}
      <input
        type="file"
        accept="image/*,user"
        capture="user"
        data-camera="front"
        id="attendanceCamera"
        hidden
        onChange={handleSelfieCapture}
      />
      <input
        type="file"
        accept="image/*,environment"
        capture="environment"
        data-camera="back"
        id="odoCamera"
        hidden
        ref={odoCameraRef}
        onChange={handleOdoCapture}
      />
    </div>
  );
};

export default DashboardPage;